'use client';

import type { GeometryFigureBlock, GeometryObject } from '@jeraisonne/ai/visual-blocks';
import { figureCenter, segmentLabelPlacement } from './segment-label';

const PADDING = 32;

function computeViewBox(objects: GeometryObject[]) {
  const xs: number[] = [];
  const ys: number[] = [];

  for (const obj of objects) {
    if (obj.type === 'point') {
      xs.push(obj.x);
      ys.push(obj.y);
    }
  }

  if (xs.length === 0) return { minX: 0, minY: 0, width: 200, height: 200 };

  const minX = Math.min(...xs) - PADDING;
  const maxX = Math.max(...xs) + PADDING;
  const minY = Math.min(...ys) - PADDING;
  const maxY = Math.max(...ys) + PADDING;

  return { minX, minY, width: maxX - minX, height: maxY - minY };
}

export function GeometryFigureBlockRenderer({ block }: { block: GeometryFigureBlock }) {
  const points = Object.fromEntries(
    block.objects
      .filter((o): o is Extract<GeometryObject, { type: 'point' }> => o.type === 'point')
      .map((p) => [p.id, p]),
  );

  const { minX, minY, width, height } = computeViewBox(block.objects);

  // Flip Y so screen coords match math coords
  function tx(x: number) {
    return x - minX;
  }
  function ty(y: number) {
    return height - (y - minY);
  }
  function pt(id: string) {
    const p = points[id];
    return p ? { x: tx(p.x), y: ty(p.y) } : null;
  }
  // Cotes des segments décalées vers l'extérieur de la figure (jamais sur le trait).
  const center = figureCenter(Object.values(points).map((p) => ({ x: tx(p.x), y: ty(p.y) })));

  return (
    <div className="w-full overflow-x-auto">
      <svg
        viewBox={`0 0 ${width} ${height}`}
        className="mx-auto w-full max-w-lg"
        style={{ maxHeight: '360px' }}
        role="img"
        aria-label={block.caption ?? block.title ?? 'Figure géométrique'}
      >
        {block.objects.map((obj) => {
          if (obj.type === 'segment') {
            const A = pt(obj.from);
            const B = pt(obj.to);
            if (!A || !B) return null;
            return (
              <g key={obj.id}>
                <line
                  x1={A.x}
                  y1={A.y}
                  x2={B.x}
                  y2={B.y}
                  stroke={obj.color ?? '#374151'}
                  strokeWidth={1}
                  strokeDasharray={obj.dashed ? '6 3' : undefined}
                />
                {obj.label &&
                  (() => {
                    const lp = segmentLabelPlacement(A, B, center, 4);
                    return (
                      <text
                        x={lp.x}
                        y={lp.y}
                        dy={lp.dy}
                        textAnchor={lp.textAnchor}
                        fontSize={5}
                        fill={obj.color ?? '#374151'}
                      >
                        {obj.label}
                      </text>
                    );
                  })()}
              </g>
            );
          }

          if (obj.type === 'line') {
            const A = pt(obj.through[0]);
            const B = pt(obj.through[1]);
            if (!A || !B) return null;
            // Extend line across the SVG
            const dx = B.x - A.x;
            const dy = B.y - A.y;
            const t1 = -Math.max(width, height) * 2;
            const t2 = Math.max(width, height) * 2;
            return (
              <line
                key={obj.id}
                x1={A.x + dx * t1}
                y1={A.y + dy * t1}
                x2={A.x + dx * t2}
                y2={A.y + dy * t2}
                stroke={obj.color ?? '#374151'}
                strokeWidth={0.5}
                strokeDasharray={obj.dashed ? '6 3' : undefined}
              />
            );
          }

          if (obj.type === 'circle') {
            const c = pt(obj.center);
            if (!c) return null;
            return (
              <circle
                key={obj.id}
                cx={c.x}
                cy={c.y}
                r={obj.radius}
                fill="none"
                stroke={obj.color ?? '#374151'}
                strokeWidth={1}
              />
            );
          }

          if (obj.type === 'polygon') {
            const pts = obj.points.map((id) => pt(id)).filter(Boolean) as {
              x: number;
              y: number;
            }[];
            if (pts.length < 2) return null;
            const d = pts.map((p, i) => `${i === 0 ? 'M' : 'L'} ${p.x} ${p.y}`).join(' ') + ' Z';
            return (
              <path
                key={obj.id}
                d={d}
                fill={obj.fill ?? 'none'}
                stroke={obj.stroke ?? '#374151'}
                strokeWidth={1}
              />
            );
          }

          if (obj.type === 'angle') {
            const [vertex, p1, p2] = obj.points.map((id) => pt(id));
            if (!vertex || !p1 || !p2) return null;
            const r = 18;
            const angle1 = Math.atan2(p1.y - vertex.y, p1.x - vertex.x);
            const angle2 = Math.atan2(p2.y - vertex.y, p2.x - vertex.x);
            const ax1 = vertex.x + r * Math.cos(angle1);
            const ay1 = vertex.y + r * Math.sin(angle1);
            const ax2 = vertex.x + r * Math.cos(angle2);
            const ay2 = vertex.y + r * Math.sin(angle2);
            return (
              <g key={obj.id}>
                <path
                  d={`M ${ax1} ${ay1} A ${r} ${r} 0 0 1 ${ax2} ${ay2}`}
                  fill="none"
                  stroke={obj.color ?? '#374151'}
                  strokeWidth={1.5}
                />
                {obj.label && (
                  <text
                    x={vertex.x + (r + 10) * Math.cos((angle1 + angle2) / 2)}
                    y={vertex.y + (r + 10) * Math.sin((angle1 + angle2) / 2)}
                    textAnchor="middle"
                    dominantBaseline="middle"
                    fontSize={5}
                    fill={obj.color ?? '#374151'}
                  >
                    {obj.label}
                  </text>
                )}
              </g>
            );
          }

          if (obj.type === 'point') {
            const p = pt(obj.id);
            if (!p) return null;
            return (
              <g key={obj.id}>
                <circle cx={p.x} cy={p.y} r={1} fill={obj.color ?? '#374151'} />
                {obj.name && (
                  <text
                    x={p.x + (obj.offsetLabelX ?? 4)}
                    y={p.y + +(obj.offsetLabelY ?? -4)}
                    fontSize={5}
                    fontWeight={600}
                    fill={obj.color ?? '#374151'}
                  >
                    {obj.name}
                  </text>
                )}
              </g>
            );
          }

          return null;
        })}
      </svg>
    </div>
  );
}
